import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CreditCard, DollarSign, Smartphone, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { Logo } from '../components/LogoOptions';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { submissionId, packageId, amount, serviceType } = location.state || {};

  const [paymentConfig, setPaymentConfig] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState('stripe');

  useEffect(() => {
    if (!submissionId) {
      navigate('/');
      return;
    }

    // Fetch payment configuration
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/payment/config`);
        setPaymentConfig(response.data);
      } catch (error) {
        console.error('Failed to fetch payment config:', error);
        toast.error('Failed to load payment options');
      }
    };

    fetchConfig();
  }, [submissionId, navigate]);

  const handleStripePayment = async () => {
    setIsProcessing(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/payment/create-checkout`, {
        package_id: packageId,
        submission_id: submissionId,
        origin_url: originUrl
      });

      if (response.data.success) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } else {
        toast.error(response.data.message || 'Failed to create checkout session');
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to process payment');
      setIsProcessing(false);
    }
  };

  const getPaymentAmount = () => {
    return amount || 300;
  };

  const getDiscount = () => {
    return packageId === 'listing_buying_pay_now' ? 30 : 0;
  };

  if (!paymentConfig) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <Logo />
        </div>
      </header>

      {/* Payment Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Complete Your Payment</h1>
            <p className="text-gray-600">{serviceType}</p>
          </div>

          {/* Price Summary */}
          <Card className="p-6 mb-8 bg-gradient-to-br from-amber-50 to-white border-2 border-amber-200">
            <div className="space-y-3">
              <div className="flex justify-between items-center text-lg">
                <span className="text-gray-700">Service Fee:</span>
                <span className="font-semibold">${(getPaymentAmount() + getDiscount()).toFixed(2)}</span>
              </div>
              {getDiscount() > 0 && (
                <div className="flex justify-between items-center text-lg text-green-600">
                  <span className="font-semibold">Early Payment Discount (10%):</span>
                  <span className="font-semibold">-${getDiscount().toFixed(2)}</span>
                </div>
              )}
              <div className="border-t-2 border-amber-300 pt-3 flex justify-between items-center text-2xl font-bold">
                <span className="text-gray-900">Total Due:</span>
                <span className="text-amber-600">${getPaymentAmount().toFixed(2)}</span>
              </div>
            </div>
          </Card>

          {/* Payment Methods */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {/* Stripe Card */}
            <Card 
              className={`p-6 cursor-pointer transition-all duration-300 ${
                selectedMethod === 'stripe' 
                  ? 'border-2 border-amber-500 shadow-lg' 
                  : 'border-2 border-gray-200 hover:border-amber-300'
              }`}
              onClick={() => setSelectedMethod('stripe')}
            >
              <div className="text-center">
                <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                  selectedMethod === 'stripe' ? 'bg-amber-500' : 'bg-gray-100'
                }`}>
                  <CreditCard className={`w-8 h-8 ${selectedMethod === 'stripe' ? 'text-white' : 'text-gray-600'}`} />
                </div>
                <h3 className="font-bold text-lg mb-2">Credit Card</h3>
                <p className="text-sm text-gray-600">Pay securely with Stripe</p>
                {selectedMethod === 'stripe' && (
                  <CheckCircle2 className="w-6 h-6 text-amber-500 mx-auto mt-3" />
                )}
              </div>
            </Card>

            {/* Venmo Card */}
            <Card 
              className={`p-6 cursor-pointer transition-all duration-300 ${
                selectedMethod === 'venmo' 
                  ? 'border-2 border-amber-500 shadow-lg' 
                  : 'border-2 border-gray-200 hover:border-amber-300'
              }`}
              onClick={() => setSelectedMethod('venmo')}
            >
              <div className="text-center">
                <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                  selectedMethod === 'venmo' ? 'bg-amber-500' : 'bg-gray-100'
                }`}>
                  <Smartphone className={`w-8 h-8 ${selectedMethod === 'venmo' ? 'text-white' : 'text-gray-600'}`} />
                </div>
                <h3 className="font-bold text-lg mb-2">Venmo</h3>
                <p className="text-sm text-gray-600">Quick mobile payment</p>
                {selectedMethod === 'venmo' && (
                  <CheckCircle2 className="w-6 h-6 text-amber-500 mx-auto mt-3" />
                )}
              </div>
            </Card>

            {/* Zelle Card */}
            <Card 
              className={`p-6 cursor-pointer transition-all duration-300 ${
                selectedMethod === 'zelle' 
                  ? 'border-2 border-amber-500 shadow-lg' 
                  : 'border-2 border-gray-200 hover:border-amber-300'
              }`}
              onClick={() => setSelectedMethod('zelle')}
            >
              <div className="text-center">
                <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                  selectedMethod === 'zelle' ? 'bg-amber-500' : 'bg-gray-100'
                }`}>
                  <DollarSign className={`w-8 h-8 ${selectedMethod === 'zelle' ? 'text-white' : 'text-gray-600'}`} />
                </div>
                <h3 className="font-bold text-lg mb-2">Zelle</h3>
                <p className="text-sm text-gray-600">Bank transfer</p>
                {selectedMethod === 'zelle' && (
                  <CheckCircle2 className="w-6 h-6 text-amber-500 mx-auto mt-3" />
                )}
              </div>
            </Card>
          </div>

          {/* Payment Instructions */}
          {selectedMethod === 'stripe' && (
            <Card className="p-6 mb-6">
              <h3 className="font-bold text-lg mb-4">Pay with Credit Card</h3>
              <p className="text-gray-600 mb-6">
                Click the button below to securely complete your payment through Stripe. You'll be redirected to our secure payment page.
              </p>
              <Button
                onClick={handleStripePayment}
                disabled={isProcessing}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 text-lg font-bold"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    Pay ${getPaymentAmount().toFixed(2)} with Stripe
                  </>
                )}
              </Button>
            </Card>
          )}

          {selectedMethod === 'venmo' && (
            <Card className="p-6 mb-6 bg-blue-50 border-2 border-blue-200">
              <h3 className="font-bold text-lg mb-4">Pay with Venmo</h3>
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-lg border border-blue-300">
                  <p className="text-sm text-gray-600 mb-2">Send payment to:</p>
                  <p className="text-xl font-bold text-gray-900">{paymentConfig.venmo_email}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-blue-300">
                  <p className="text-sm text-gray-600 mb-2">Amount:</p>
                  <p className="text-2xl font-bold text-amber-600">${getPaymentAmount().toFixed(2)}</p>
                </div>
                <div className="bg-amber-100 border-l-4 border-amber-500 p-4">
                  <p className="font-semibold text-amber-900 mb-2">📱 Instructions:</p>
                  <ol className="text-sm text-amber-800 space-y-2 ml-4 list-decimal">
                    <li>Open your Venmo app</li>
                    <li>Search for or enter: <strong>{paymentConfig.venmo_email}</strong></li>
                    <li>Enter amount: <strong>${getPaymentAmount().toFixed(2)}</strong></li>
                    <li>Add note: "Transaction Coordination - [Property Address]"</li>
                    <li>Complete the payment</li>
                    <li>Screenshot your confirmation and email to info@ezflowtc.com</li>
                  </ol>
                </div>
                <Button
                  onClick={() => {
                    toast.success('Please complete payment via Venmo and email confirmation');
                    navigate('/');
                  }}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg font-bold"
                >
                  I've Sent Payment via Venmo
                </Button>
              </div>
            </Card>
          )}

          {selectedMethod === 'zelle' && (
            <Card className="p-6 mb-6 bg-purple-50 border-2 border-purple-200">
              <h3 className="font-bold text-lg mb-4">Pay with Zelle</h3>
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-lg border border-purple-300">
                  <p className="text-sm text-gray-600 mb-2">Send payment to:</p>
                  <p className="text-xl font-bold text-gray-900">{paymentConfig.zelle_phone}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-purple-300">
                  <p className="text-sm text-gray-600 mb-2">Amount:</p>
                  <p className="text-2xl font-bold text-amber-600">${getPaymentAmount().toFixed(2)}</p>
                </div>
                <div className="bg-amber-100 border-l-4 border-amber-500 p-4">
                  <p className="font-semibold text-amber-900 mb-2">🏦 Instructions:</p>
                  <ol className="text-sm text-amber-800 space-y-2 ml-4 list-decimal">
                    <li>Open your bank's mobile app</li>
                    <li>Go to Zelle (usually under "Send Money")</li>
                    <li>Enter phone: <strong>{paymentConfig.zelle_phone}</strong></li>
                    <li>Enter amount: <strong>${getPaymentAmount().toFixed(2)}</strong></li>
                    <li>Add memo: "Transaction Coordination - [Property Address]"</li>
                    <li>Complete the payment</li>
                    <li>Screenshot your confirmation and email to info@ezflowtc.com</li>
                  </ol>
                </div>
                <Button
                  onClick={() => {
                    toast.success('Please complete payment via Zelle and email confirmation');
                    navigate('/');
                  }}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white py-6 text-lg font-bold"
                >
                  I've Sent Payment via Zelle
                </Button>
              </div>
            </Card>
          )}

          <div className="text-center text-sm text-gray-500 mt-6">
            <p>Need help? Call us at 206-627-0768 or email info@ezflowtc.com</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
